import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Plus, Trash2, User } from 'lucide-react';

const STATUS_COLORS = {
  'Empty in Customer': '#FFDAB9',
  'Available for Pickup': '#90EE90',
  'Need Appt': '#1a1a1a',
  'On the way to Customer': '#FFB6C1',
  'Important Customer': '#A0522D',
  'Delivered to Customer': '#FFC0CB',
  'In Yard': '#DDA0DD',
  'Container Returned': '#D3D3D3',
  'On the way to Return': '#AFEEEE',
  'Delivered to Exam Location': '#8B4513',
  'New Shipment': '#E6E6FA',
  'Empty Container in Yard': '#FFFFE0',
  'On the way to Pickup': '#228B22',
  'Shipment Closed': '#ADD8E6',
  'Trouble': '#FA8072',
};

const DARK_TEXT_STATUSES = ['Need Appt', 'Important Customer', 'Delivered to Exam Location', 'On the way to Pickup'];

// Status History Tab (Export variant) — adds a coloured status badge so the
// log row visually matches the dashboard legend. Same shape as the Load version.
export const StatusHistoryTab = ({
  statusHistory,
  formData,
  onChangeStatus,
  onDeleteEntry,
  formatDateTime,
}) => (
  <div className="space-y-4">
    <div className="flex items-center gap-3">
      <Button size="sm" className="bg-orange-500 hover:bg-orange-400" onClick={onChangeStatus} data-testid="change-status-btn">
        <Plus className="h-4 w-4 mr-1" />Change Status
      </Button>
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <span>Current:</span>
        <Badge variant="outline" className="text-white">{formData.status}</Badge>
        {formData.driver_name && (
          <>
            <User className="h-4 w-4 ml-2" />
            <span>{formData.driver_name}</span>
          </>
        )}
      </div>
    </div>

    <Table className="data-table">
      <TableHeader>
        <TableRow className="border-slate-800">
          <TableHead className="text-slate-400">Status</TableHead>
          <TableHead className="text-slate-400">Event TimeStamp</TableHead>
          <TableHead className="text-slate-400">Effective Date</TableHead>
          <TableHead className="text-slate-400">Driver</TableHead>
          <TableHead className="text-slate-400">Message</TableHead>
          <TableHead className="text-slate-400">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {statusHistory.map(entry => (
          <TableRow key={entry.id} className="border-slate-800">
            <TableCell>
              <Badge
                style={{ backgroundColor: STATUS_COLORS[entry.status] || '#94a3b8' }}
                className={DARK_TEXT_STATUSES.includes(entry.status) ? 'text-white' : 'text-black'}
              >
                {entry.status}
              </Badge>
            </TableCell>
            <TableCell className="text-slate-300">{formatDateTime(entry.created_at)}</TableCell>
            <TableCell className="text-slate-300">{entry.effective_date || '-'}</TableCell>
            <TableCell className="text-slate-300">{entry.driver_name || '-'}</TableCell>
            <TableCell className="text-slate-300 max-w-[200px] truncate">{entry.message || '-'}</TableCell>
            <TableCell>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDeleteEntry?.(entry.id)}
                className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
        {statusHistory.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} className="text-center text-slate-500 py-8">No status history yet</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
    <p className="text-sm text-slate-500">Total {statusHistory.length} records</p>
  </div>
);
